// Full enhanced UI version of AccountDetails with Bootstrap improvements
// Original component names retained

import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Table,
  Form,
  Badge,
  Spinner,
  Alert,
} from "react-bootstrap";
import {
  fetchSubscriptions,
  subscribeAccount,
  fetchAccounts,
  fetchAccountSubscriptions,
  deactivateAccountSubscription,
  createOrUpdateAccount,
  fetchUsersForAccount,
  removeUserFromAccount,
  addUserToAccount,
} from "../../utilities/AdminUtilities";
import LoadingComponent from "../Common/LoadingComponent";
import { Field, initials } from "./AdminUi";

function AccountDetails() {
  const [accounts, setAccounts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [name, setName] = useState("");
  const [referenceId, setReferenceId] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [accountSubscriptions, setAccountSubscriptions] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [detailView, setDetailView] = useState("active");
  const [activeSubscription, setActiveSubscription] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    for_reference_type: "",
    for_reference_id: "",
    account_type: "",
  });

  useEffect(() => {
    fetchSubscriptions(setSubscriptions);
  }, []);

  useEffect(() => {
    setLoadingAccounts(true);
    fetchAccounts(name, referenceId, setAccounts, setLoadingAccounts);
  }, [name, referenceId, currentPage]);

  const handleRowClick = async (account) => {
    setSelectedAccount(account);
    setDetailView("active");
    setLoadingSubscriptions(true);
    fetchAccountSubscriptions(
      account.id,
      (subs) => setAccountSubscriptions(subs),
      setLoadingSubscriptions
    );
    setShowDetailModal(true);
  };

  useEffect(() => {
    setActiveSubscription(
      subscriptions.filter((sub) =>
        accountSubscriptions.some((accSub) => accSub.subscription_id === sub.id)
      )
    );
  }, [accountSubscriptions]);

  const handleSubscribe = async (sub, account) => {
    const ok = await subscribeAccount(sub, account);
    if (ok) {
      fetchAccountSubscriptions(
        account.id,
        (subs) => setAccountSubscriptions(subs),
        setLoadingSubscriptions
      );
    }
  };

  const handleUnsubscribe = async (sub, account) => {
    try {
      await deactivateAccountSubscription({
        accountId: account.id,
        subscriptionId: sub.id,
      });
      alert(`Unsubscribed ${account.name} from ${sub.subscription_name}`);
      setShowDetailModal(false);
    } catch (error) {
      alert("Failed to unsubscribe.");
    }
  };

  const handleDeleteAccount = async () => {
    alert("Not Yet Implemented");
    setShowDetailModal(false);
    fetchAccounts(name, referenceId, setAccounts, setLoadingAccounts);
  };

  const handleAddEditClick = (account = null) => {
    if (account) {
      setFormData({
        id: account.id,
        name: account.name || "",
        for_reference_type: account.for_reference_type || "",
        for_reference_id: account.for_reference_id || "",
        account_type: account.account_type || "",
      });
    } else {
      setFormData({
        id: "",
        name: "",
        for_reference_type: "",
        for_reference_id: "",
        account_type: "",
      });
    }
    setShowEditModal(true);
  };

  const handleFormSubmit = () => {
    createOrUpdateAccount({ formData });
    setShowEditModal(false);
    fetchAccounts(name, referenceId, setAccounts, setLoadingAccounts);
  };

  const subscribed = subscriptions.filter((sub) =>
    accountSubscriptions.some((accSub) => accSub.subscription_id === sub.id)
  );
  const unsubscribed = subscriptions.filter(
    (sub) =>
      !accountSubscriptions.some((accSub) => accSub.subscription_id === sub.id)
  );

  const formatCreated = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    return Number.isNaN(d.getTime())
      ? String(v)
      : d.toLocaleString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
  };

  const validityText = (sub) =>
    sub.validity === -1 ? "Unlimited" : `${sub.validity} days`;

  const typeBadges = (types) =>
    (types || []).length ? (
      <span className="ac-types">
        {(Array.isArray(types) ? types : [types]).map((t) => (
          <Badge key={t} bg="secondary" pill>
            {t}
          </Badge>
        ))}
      </span>
    ) : (
      "—"
    );

  const formField = (label, key) => (
    <Form.Group className="ep-group">
      <Form.Label className="pv-label">{label}</Form.Label>
      <Form.Control
        type="text"
        value={formData[key]}
        onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
      />
    </Form.Group>
  );

  return (
    <>
      <div className="up-toolbar">
        <div className="up-filters">
          <Form.Control
            className="up-filter-wide"
            type="text"
            placeholder="Search by name"
            aria-label="Search by name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Form.Control
            className="up-filter-wide"
            type="text"
            placeholder="Search by reference ID"
            aria-label="Search by reference ID"
            value={referenceId}
            onChange={(e) => setReferenceId(e.target.value)}
          />
          {(name || referenceId) && (
            <Button
              variant="link"
              className="up-clear"
              onClick={() => {
                setName("");
                setReferenceId("");
              }}
            >
              Clear
            </Button>
          )}
        </div>
        <Button variant="primary" onClick={() => handleAddEditClick()}>
          + Add Account
        </Button>
      </div>

      <div className="up-card">
        <Table hover responsive className="up-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Reference Type</th>
              <th>Reference ID</th>
              <th>Account Type</th>
              <th>Created</th>
              <th>ID</th>
            </tr>
          </thead>
          <tbody>
            {loadingAccounts ? (
              <tr>
                <td colSpan="6" className="text-center">
                  <LoadingComponent />
                </td>
              </tr>
            ) : accounts.length ? (
              accounts.map((account) => (
                <tr
                  key={account.id}
                  className="up-row"
                  onClick={() => handleRowClick(account)}
                >
                  <td className="up-name">{account.name || "—"}</td>
                  <td>{account.for_reference_type || "—"}</td>
                  <td>{account.for_reference_id || "—"}</td>
                  <td>{typeBadges(account.account_type)}</td>
                  <td className="up-date">{formatCreated(account.created_at)}</td>
                  <td className="up-email pv-mono">{account.id}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="up-empty">
                  No accounts found.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
      >
        <Modal.Header closeButton className="pv-header">
          <div>
            <div className="pv-eyebrow">
              {formData.id ? "Edit Account" : "New Account"}
            </div>
            <div className="pv-title up-title text-break">
              {formData.name || "Untitled account"}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form className="d-flex flex-column gap-3">
            {formField("Name", "name")}
            {formField("Reference Type", "for_reference_type")}
            {formField("Reference ID", "for_reference_id")}
            {formField("Account Type", "account_type")}
          </Form>
        </Modal.Body>
        <Modal.Footer className="ep-footer">
          <Button
            variant="outline-secondary"
            onClick={() => setShowEditModal(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleFormSubmit}>
            {formData.id ? "Save Changes" : "Create Account"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Detail Modal */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton className="pv-header">
          <div className="up-modal-head">
            <span className="up-avatar up-avatar-lg">
              {initials({ first_name: selectedAccount?.name })}
            </span>
            <div className="min-w-0">
              <div className="pv-eyebrow">Account</div>
              <div className="pv-title up-title text-break">
                {selectedAccount?.name || "Account"}
              </div>
              <div className="up-modal-sub pv-mono text-break">
                {selectedAccount?.id}
              </div>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {selectedAccount && (
            <>
              <div className="pv-section-title">Details</div>
              <div className="pv-grid">
                <Field label="Account Type">
                  {typeBadges(selectedAccount.account_type)}
                </Field>
                <Field
                  label="Created"
                  value={formatCreated(selectedAccount.created_at)}
                />
                <Field
                  label="Reference Type"
                  value={selectedAccount.for_reference_type}
                />
                <Field
                  label="Reference ID"
                  value={selectedAccount.for_reference_id}
                  mono
                  wide
                />
              </div>
            </>
          )}

          <div className="up-subhead">
            <div className="up-segment" role="tablist">
              <button
                type="button"
                className={detailView === "active" ? "is-on" : ""}
                onClick={() => setDetailView("active")}
              >
                Active ({subscribed.length})
              </button>
              <button
                type="button"
                className={detailView === "available" ? "is-on" : ""}
                onClick={() => setDetailView("available")}
              >
                Available ({unsubscribed.length})
              </button>
              <button
                type="button"
                className={detailView === "users" ? "is-on" : ""}
                onClick={() => setDetailView("users")}
              >
                Users
              </button>
            </div>
          </div>

          {detailView === "active" &&
            (loadingSubscriptions ? (
              <div className="text-center my-4">
                <Spinner animation="border" variant="primary" size="sm" />
              </div>
            ) : subscribed.length ? (
              <Table responsive size="sm" className="up-subtable">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Validity</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {activeSubscription.map((sub) => (
                    <tr key={sub.id}>
                      <td className="fw-semibold">{sub.subscription_name}</td>
                      <td>{validityText(sub)}</td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleUnsubscribe(sub, selectedAccount)}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="up-empty">No active subscriptions.</div>
            ))}

          {detailView === "available" &&
            (unsubscribed.length > 0 ? (
              <Table responsive size="sm" className="up-subtable">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Details</th>
                    <th>Validity</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {unsubscribed.map((sub) => (
                    <tr key={sub.id}>
                      <td className="fw-semibold">{sub.subscription_name}</td>
                      <td>{sub.details || "—"}</td>
                      <td>{validityText(sub)}</td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleSubscribe(sub, selectedAccount)}
                        >
                          Subscribe
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="up-empty">No new subscriptions available.</div>
            ))}

          {/* Kept mounted so switching tabs doesn't refetch */}
          <div hidden={detailView !== "users"}>
            <AccountUsersList accountId={selectedAccount?.id} />
          </div>
        </Modal.Body>
        <Modal.Footer className="ep-footer">
          <Button
            variant="outline-danger"
            className="me-auto"
            onClick={handleDeleteAccount}
          >
            Delete Account
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => setShowDetailModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default AccountDetails;

const AccountUsersList = ({ accountId }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");

  useEffect(() => {
    if (!accountId) return;

    setLoading(true);
    setError(null);

    fetchUsersForAccount({ accountId })
      .then(setUsers)
      .catch(() => setError("Failed to fetch users"))
      .finally(() => setLoading(false));
  }, [accountId]);

  const handleAddUser = () => {
    addUserToAccount({ accountId, userEmail: newUserEmail }).then(() => {
      fetchUsersForAccount({ accountId }).then(setUsers);
      alert(`Add user with email: ${newUserEmail} to account: ${accountId}`);
    }).catch(() => {
      alert("Failed to add user to account");
    })
    setNewUserEmail("");
    setShowAddUser(false);
  };

  const handleRemoveUser = (userId) => {
    removeUserFromAccount({ accountId, userId })
      .then(() => {
        setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userId));
      })
      .catch(() => {
        alert("Failed to remove user from account");
      });
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="text-muted small">
          {loading ? "Loading users…" : `${users.length} user${users.length === 1 ? "" : "s"}`}
        </div>
        <Button
          size="sm"
          variant={showAddUser ? "outline-secondary" : "outline-primary"}
          onClick={() => setShowAddUser(!showAddUser)}
        >
          {showAddUser ? "Cancel" : "+ Add User"}
        </Button>
      </div>

      {showAddUser && (
        <div className="d-flex gap-2 mb-3">
          <Form.Control
            type="email"
            placeholder="Enter user email"
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
          />
          <Button variant="primary" onClick={handleAddUser}>
            Add
          </Button>
        </div>
      )}

      {loading ? (
        <div className="text-center my-4">
          <Spinner animation="border" variant="primary" size="sm" />
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : !users.length ? (
        <div className="up-empty">No users found for this account.</div>
      ) : (
        <Table responsive size="sm" className="up-subtable">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th className="text-end">Action</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>
                  <div className="up-person">
                    <span className="up-avatar">{initials(user)}</span>
                    <span className="up-name">
                      {[user.first_name, user.last_name].filter(Boolean).join(" ") || "—"}
                    </span>
                  </div>
                </td>
                <td className="up-email">{user.email}</td>
                <td>{user.phone_number || "—"}</td>
                <td>
                  <Badge bg={user.role === "ADMIN" ? "success" : "secondary"} pill>
                    {user.role}
                  </Badge>
                </td>
                <td className="text-end">
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => handleRemoveUser(user.id)}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};
